"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, ArrowRight, FileCheck2, Cpu, ShieldCheck, Headset, Layers, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
const phases = [{
  phase: "Phase 01",
  title: "Method Compatibility Assessment",
  description: "Detailed evaluation of your target analytical workflows, column chemistries, sensitivity thresholds, and sample matrices to verify instrument suitability.",
  deliverables: "Chemistry matrix review, throughput estimation, and pre-deployment validation plan.",
  icon: FileCheck2
}, {
  phase: "Phase 02",
  title: "Hardware & Fluidic Interfacing",
  description: "Physical positioning, custom plumbing, precision fluidic routing, and digital integration with existing chromatography and spectrometer networks.",
  deliverables: "Low dead-volume fluidic paths, baseline pressure balancing, and system interconnects.",
  icon: Cpu
}, {
  phase: "Phase 03",
  title: "Qualification & Baseline Verification",
  description: "Complete IQ/OQ/PQ execution adhering strictly to European and UK pharmacopoeial and GMP data integrity compliance standards.",
  deliverables: "Executed qualification protocols, baseline noise audits, and signed compliance certificates.",
  icon: ShieldCheck
}, {
  phase: "Phase 04",
  title: "Ongoing Maintenance & Operational Support",
  description: "Preventative service schedules, rapid-response field support across Ireland and the UK, plus structured operator training modules.",
  deliverables: "SLA coverage agreements, scheduled preventative visits, and local spare parts priority.",
  icon: Headset
}];
const highlights = [{
  label: "Compliance Focus",
  value: "GMP / GLP & Data Integrity"
}, {
  label: "Deployment Footprint",
  value: "Ireland & UK Field Coverage"
}, {
  label: "Response Window",
  value: "< 24h Engineering Dispatch"
}];
export default function OnboardingPathway() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 18
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="onboardingpathway" data-nav="light" className="bg-background py-16 md:py-24 border-b border-border text-foreground">
      <div className="page-container">
        {/* Section Header */}
        <div className="mb-12 md:mb-16 max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold mb-4">
            <Layers className="size-3.5" />
            <span>Structured Implementation Lifecycle</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground">
            Structured 4-Phase Analytical Onboarding
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            From technical feasibility to certified routine production, our engineering pathway delivers seamless integration for regulated laboratory instrumentation.
          </p>
        </div>

        {/* 8/4 Grid Split */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          {/* Left Column: 8 Cols - The 4 Phases */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.15
        }} className="lg:col-span-8 flex flex-col gap-6">
            {phases.map((item, i) => {
            const IconComponent = item.icon;
            return <motion.div key={item.phase} variants={itemVariants} data-index={i}>
                  <Card className="border border-border bg-card shadow-xs hover:border-primary/40 hover:shadow-md transition-[border-color,box-shadow] duration-200">
                    <CardHeader className="pb-3">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <span className="flex items-center justify-center size-9 rounded-lg bg-primary/10 text-primary border border-primary/20">
                            <IconComponent className="size-4.5" />
                          </span>
                          <div>
                            <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                              {item.phase}
                            </span>
                            <CardTitle className="text-lg sm:text-xl font-default font-bold text-card-foreground mt-0.5">
                              {item.title}
                            </CardTitle>
                          </div>
                        </div>
                        <Badge variant="secondary" className="text-[11px] font-medium">
                          Step 0{i + 1} of 04
                        </Badge>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="pt-1 flex flex-col gap-4">
                      <CardDescription className="text-sm sm:text-base leading-relaxed text-muted-foreground">
                        {item.description}
                      </CardDescription>

                      <div className="rounded-lg bg-secondary/60 p-3.5 border border-border/70 flex items-start gap-2.5">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <div className="text-xs sm:text-sm text-foreground">
                          <strong className="font-semibold text-foreground">Key Output: </strong>
                          <span className="text-muted-foreground">{item.deliverables}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </motion.div>

          {/* Right Column: 4 Cols - Supporting Context & CTA */}
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="lg:col-span-4 flex flex-col gap-6 lg:sticky lg:top-24">
            {/* Context Summary Card */}
            <Card className="border border-border bg-card shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2 text-primary text-xs font-semibold uppercase tracking-wider">
                  <Sparkles className="size-3.5" />
                  <span>Engineering Standard</span>
                </div>
                <CardTitle className="text-xl font-default font-bold text-card-foreground">
                  Application-Led Precision
                </CardTitle>
                <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                  Our onboarding methodology eliminates qualification bottlenecks and ensures instruments reach validated operation with zero downtime.
                </CardDescription>
              </CardHeader>

              <CardContent className="flex flex-col gap-4">
                <Separator />
                
                <div className="space-y-3.5">
                  {highlights.map((h, i) => <div key={h.label} data-index={i} className="flex flex-col gap-1">
                      <span className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
                        {h.label}
                      </span>
                      <span className="text-sm font-semibold text-foreground">
                        {h.value}
                      </span>
                    </div>)}
                </div>

                <Separator />

                <div className="pt-2 flex flex-col gap-3">
                  <Button asChild size="lg" className="w-full justify-between">
                    <Link to="/ContactPage">
                      <span>Schedule Technical Review</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                  
                  <Button asChild variant="outline" size="sm" className="w-full">
                    <Link to="/SolutionsPage">
                      <span>Explore Technical Solutions</span>
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Quick Note Badge Card */}
            <div className="p-4 rounded-xl bg-secondary/80 border border-border flex items-start gap-3">
              <span className="size-2 rounded-full bg-primary mt-1.5 shrink-0" />
              <p className="text-xs leading-relaxed text-muted-foreground">
                All qualification and verification protocols can be customized to align with your facility SOPs and change control guidelines.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}